/**
 * SubscriptionGate Component
 *
 * Wrapper for routes that require an active subscription. Free users hitting
 * a gated route get the upgrade modal popped (via SubscriptionContext) AND
 * bounced to /dashboard so they're not stranded on a dead-end /subscription-required
 * page. The modal is mounted globally at App root, so it floats over the
 * dashboard while preserving the user's place in the app.
 *
 * Still shows the SubscriptionWarningBanner for canceled-but-active subs
 * (those users have legitimate paid access; the banner just warns about
 * upcoming expiration).
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { X, AlertTriangle } from 'react-feather';

// Map URL paths to the action label handed to the upgrade modal. UpgradeModal
// only prints it ("Upgrade to <action>") when it's a FEATURE_COPY key (e.g.
// "Analytics"); anything else gets the generic "Upgrade to unlock this"
// header. The label is also the paywall trigger_action in analytics and the
// Stripe checkout metadata. Falls back to "APS Membership features" (the one
// plan since Premium retired 2026-09-11) for paths not in the map.
const PATH_TO_LABEL: Record<string, string> = {
  '/messages': 'Messages',
  '/analytics': 'Analytics',
  '/colleges': 'College Lists',
};
const labelForPath = (pathname: string): string => {
  // Match by prefix so /messages/123 still resolves to "Messages".
  const hit = Object.keys(PATH_TO_LABEL).find((p) => pathname.startsWith(p));
  return hit ? PATH_TO_LABEL[hit] : 'APS Membership features';
};

interface SubscriptionGateProps {
  children: React.ReactNode;
}

// Warning banner component
const SubscriptionWarningBanner: React.FC<{
  expirationDate: string | null;
  onDismiss: () => void;
  onManage?: () => void;
}> = ({ expirationDate, onDismiss, onManage }) => {
  const formatDate = (date: string | null): string => {
    if (!date) return 'soon';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (date: string | null): number | null => {
    if (!date) return null;
    const d = new Date(date);
    const now = new Date();
    const diffTime = d.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining(expirationDate);

  return (
    <div style={{
      // sticky (not fixed) so the banner reserves its own height in normal flow
      // — content below no longer hides under it — while still pinning on scroll.
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      background: '#FEF3C7',
      borderBottom: '1px solid #F59E0B',
      padding: '12px 16px',
      zIndex: 9999,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '18px',
          background: '#FDE68A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <AlertTriangle size={20} color="#92400E" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400E' }}>
              Subscription Ending Soon
            </span>
            {onManage && (
              <button
                onClick={onManage}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#B45309',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Manage →
              </button>
            )}
          </div>
          <div style={{ fontSize: '13px', color: '#B45309' }}>
            {daysRemaining !== null && daysRemaining <= 7
              ? `Access ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`
              : `Access ends ${formatDate(expirationDate)}`
            }
          </div>
        </div>
        <button
          onClick={onDismiss}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '16px',
            background: 'rgba(146, 64, 14, 0.1)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={20} color="#92400E" />
        </button>
      </div>
    </div>
  );
};

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Source-of-truth subscription state lives in SubscriptionContext now;
  // SubscriptionGate just reacts to it instead of fetching its own copy.
  const { isPaid, isLoaded, isCanceled, expirationDate, setModalAction } = useSubscription();

  const [showWarningBanner, setShowWarningBanner] = useState(true);

  // Trigger the upgrade modal as soon as we know a free user landed on a
  // gated route. Done in useEffect so it runs once per location change
  // rather than firing during every render.
  useEffect(() => {
    if (user?.user_type === 'staff') return;
    if (!isLoaded) return;
    if (isPaid) return;
    setModalAction(labelForPath(location.pathname));
  }, [user, isLoaded, isPaid, location.pathname, setModalAction]);

  // Staff users bypass subscription checks entirely
  if (user?.user_type === 'staff') {
    return <>{children}</>;
  }

  // Still loading auth or subscription status
  if (authLoading || !isLoaded) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#F9550D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6B7280' }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Free user on a gated route — bounce to /dashboard. The modal opened in
  // the effect above floats over the dashboard via the App-root mount,
  // giving the user the upgrade context without a dead-end page.
  if (!isPaid) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // Active but canceled subscription - show warning banner and allow access
  const showBanner = showWarningBanner && isCanceled && isPaid;

  return (
    <>
      {showBanner && (
        <SubscriptionWarningBanner
          expirationDate={expirationDate || null}
          onDismiss={() => setShowWarningBanner(false)}
          onManage={() => {
            setShowWarningBanner(false);
            navigate('?openSettings=true');
          }}
        />
      )}
      {children}
    </>
  );
};

export default SubscriptionGate;
